import React from "react"

type BaseButtonProps = {
  variant?:
    | "primary"
    | "secondary"
    | "danger"
    | "ghost"
    | "success"
    | "dropdown-trigger"
    | "cancel"
    | "outline-danger"
  size?: "xsmall" | "small" | "medium" | "large"
}

type ButtonAsButton = BaseButtonProps & {
  as?: "button"
} & React.ButtonHTMLAttributes<HTMLButtonElement>

type ButtonAsAnchor = BaseButtonProps & {
  as: "a"
} & React.AnchorHTMLAttributes<HTMLAnchorElement>

type ButtonAsSpan = BaseButtonProps & {
  as: "span"
} & React.HTMLAttributes<HTMLSpanElement>

type ButtonProps = ButtonAsButton | ButtonAsAnchor | ButtonAsSpan

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "medium",
      as = "button",
      className = "",
      children,
      ...props
    },
    ref,
  ) => {
    const baseStyles =
      "font-semibold rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 inline-flex items-center justify-center transition-all active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none disabled:hover:shadow-none disabled:active:scale-100"

    const variantStyles = {
      primary:
        "bg-primary-700 text-white hover:bg-primary-800 focus-visible:ring-primary-600 shadow-sm hover:shadow-md disabled:hover:bg-primary-700",
      secondary:
        "bg-sand-50 text-stone-700 border border-stone-300 hover:bg-sand-100 focus-visible:ring-primary-500 disabled:hover:bg-sand-50",
      danger:
        "bg-danger-600 text-white hover:bg-danger-dark focus-visible:ring-danger-600 disabled:hover:bg-danger-600",
      ghost:
        "bg-transparent text-stone-600 hover:bg-sand-50 focus-visible:ring-primary-500 disabled:hover:bg-transparent",
      success:
        "bg-success-dark text-white hover:bg-primary-800 focus-visible:ring-success-600 disabled:hover:bg-success-dark",
      "dropdown-trigger":
        "py-3 px-4 rounded-lg font-medium transition-colors duration-200 text-stone-700 hover:bg-sand-100 flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 min-h-[44px] disabled:hover:bg-transparent",
      cancel:
        "bg-sand-100 text-stone-700 hover:bg-sand-200 focus-visible:ring-stone-500 disabled:hover:bg-sand-100",
      "outline-danger":
        "bg-transparent hover:bg-danger-50 text-danger-600 font-semibold border border-danger-600 focus-visible:ring-danger-600 disabled:hover:bg-transparent",
    }

    const sizeStyles = {
      xsmall: "px-1.5 py-0.5 text-xs min-h-0",
      small: "px-3 py-2 text-sm min-h-[44px]",
      medium: "px-5 py-2.5 text-base min-h-[44px]",
      large: "px-6 py-3 text-lg min-h-[48px]",
    }

    const classes = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`

    // Only apply ref to button elements to avoid complex typing
    if (as === "button") {
      return (
        <button
          className={classes}
          ref={ref}
          {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
        >
          {children}
        </button>
      )
    }

    const Component = as
    return (
      <Component
        className={classes}
        {...(props as React.HTMLAttributes<HTMLElement>)}
      >
        {children}
      </Component>
    )
  },
)

export default Button
